import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  RefreshCw,
  Search,
  Pencil,
  Trash2,
  X,
  ChevronDown,
} from "lucide-react";
import useVendors from "../../services/Inventory/useVendors";
import { useNavigate } from "react-router-dom";
import NoIndex from "../../components/NoIndex";
import LoadingScreen from "../../utils/LoadingScreen";

const VENDOR_TYPES = [
  "Retailers",
  "Service providers",
  "Manufacturer",
  "Wholesaler",
  "Wholesalers or distributors",
  "Independent craftspeople",
  "B2c vendors",
  "Independent Vendor",
  "B2b",
  "Service vendors",
  "Retail vendors",
];

const PRODUCT_CATEGORIES = [
  "Electronics",
  "Dairy",
  "Toys",
  "Grocery",
  "Fresh Produce",
  "Meat & Seafood",
  "Bakery",
  "Beverages",
  "Frozen",
  "Household",
  "Personal Care",
  "Stationery",
  "Apparel",
  "Hardware",
  "Automotive",
  "Pharmacy",
  "Beauty",
  "Baby",
  "Pet Care",
  "Other",
];

function ScrollableSelect({
  name,
  value,
  onChange,
  options,
  placeholder,
  required,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (val) => {
    onChange({ target: { name, value: val } });
    setOpen(false);
  };

  return (
    <>
      <NoIndex />

      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full border rounded-lg px-3 py-2 text-sm text-left flex justify-between items-center bg-white"
        >
          <span className={value ? "text-gray-900" : "text-gray-400"}>
            {value || placeholder}
          </span>
          <ChevronDown
            size={14}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <ul className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-80 overflow-y-auto">
            <li
              className="px-3 py-2 text-sm text-gray-400 cursor-pointer hover:bg-gray-50"
              onClick={() => handleSelect("")}
            >
              {placeholder}
            </li>
            {options.map((opt) => (
              <li
                key={opt}
                onClick={() => handleSelect(opt)}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 
                ${value === opt ? "bg-emerald-50 text-emerald-700 font-medium" : ""}`}
              >
                {opt}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

const VendorManagement = () => {
  const {
    vendors,
    loading,
    fetchVendors,
    addVendor,
    editVendor,
    deleteVendor,
  } = useVendors();

  const [formMode, setFormMode] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [q, setQ] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);

  const [form, setForm] = useState({
    vendorName: "",
    vendorType: "",
    vendorCategory: "",
    vendorAddress: "",
    gst: "",
    mobile: "",
    state: "",
    city: "",
    vendorId: null,
  });

  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    if (formMode === "edit" && selectedVendor) {
      setForm({
        vendorId: selectedVendor.id,
        vendorName: selectedVendor.vendorName || "",
        vendorType: selectedVendor.vendorType || "",
        vendorCategory: selectedVendor.vendorCategory || "",
        vendorAddress: selectedVendor.vendorAddress || "",
        gst: selectedVendor.gst || "",
        mobile: selectedVendor.mobile || "",
        state: selectedVendor.state || "",
        city: selectedVendor.city || "",
      });
    }
  }, [selectedVendor, formMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formMode === "add") {
      addVendor(form);
    } else {
      editVendor(form);
    }

    setFormMode(null);
  };

  const handleAddClick = () => {
    setFormMode("add");
    setSelectedVendor(null);
    setForm({
      vendorName: "",
      vendorType: "",
      vendorCategory: "",
      vendorAddress: "",
      gst: "",
      mobile: "",
      state: "",
      city: "",
    });
  };

  const handleEditClick = (vendor) => {
    setFormMode("edit");
    setSelectedVendor(vendor);
  };

  const closeForm = () => {
    setFormMode(null);
  };

  const handleCreatePurchaseClick = (vendor) => {
    navigate("/Inventory/Vendors/CreatePurchase", { state: { vendor } });
  };

  const filteredVendors = useMemo(() => {
    const needle = q.toLowerCase();
    if (!needle) return vendors;

    return vendors.filter((v) =>
      JSON.stringify(v).toLowerCase().includes(needle),
    );
  }, [vendors, q]);

  const confirmDelete = async () => {
    if (!selectedVendor) return;

    await deleteVendor(selectedVendor.id);

    setDeleteModal(false);
    setSelectedVendor(null);
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchVendors();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="p-6 min-h-0 h-full flex flex-col overflow-hidden">
      {/* HEADER */}
      <NoIndex />
      <div className="flex items-center justify-between mb-6 montserrat-font-1">
        <h2 className="text-xl font-semibold text-gray-800">
          Vendor Management
        </h2>

        <div className="flex gap-3">
          <div className="flex items-center border bg-white rounded-lg px-3 py-2">
            <Search size={16} className="text-gray-400" />
            <input
              placeholder="Search vendors..."
              className="ml-2 outline-none text-sm"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <button
            onClick={handleAddClick}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg"
          >
            <Plus size={16} />
            Add Vendor
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 border rounded-lg disabled:opacity-60 flex items-center gap-2 bg-blue-500 text-white px-3 "
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* CONTENT */}

      <div className="grid grid-cols-12 gap-4 montserrat-font-1">
        {/* TABLE */}

       <div className="col-span-12 bg-white rounded-xl border shadow-sm relative flex flex-col h-[800px] overflow-hidden">
          <div className="grid grid-cols-11 text-sm font-semibold text-gray-800 bg-green-100 px-4 py-3">
            <div>Vendor Name</div>
            <div>Type</div>
            <div>Category</div>
            <div>Address</div>
            <div>GST</div>
            <div>Mobile</div>
            <div>State</div>
            <div>City</div>
            <div>Created</div>
            <div>Purchase</div>
            <div className="text-right">Actions</div>
          </div>

          {refreshing && !loading && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10">
              <LoadingScreen text="Refreshing vendors..." />
            </div>
          )}
<div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingScreen text="Loading vendors..." />
            </div>
          ) : (
            filteredVendors.map((vendor) => {
              const id = vendor?.id;

              return (
                <div
                  key={id}
                  className="grid grid-cols-11 items-center px-4 py-3 text-sm border-t hover:bg-gray-50 montserrat-font-1"
                >
                  <div className="montserrat-font-1">
                    <div className="font-medium">{vendor.vendorName}</div>
                    <div className="text-xs text-gray-400">id: {id}</div>
                  </div>

                  <div>{vendor.vendorType}</div>

                  <div>{vendor.vendorCategory}</div>

                  <div className="line-clamp-1 text-xs">
                    {vendor.vendorAddress}
                  </div>

                  <div className="font-mono text-xs">{vendor.gst}</div>

                  <div className="font-mono text-xs">{vendor.mobile}</div>

                  <div>{vendor.state}</div>

                  <div>{vendor.city}</div>

                  <div className="text-xs leading-tight">
                    <div>
                      {new Date(vendor.createdOn).toLocaleDateString("en-GB")}
                    </div>
                    <div className="text-gray-400 text-[11px]">
                      {new Date(vendor.createdOn).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </div>
                  </div>

                  <div>
                    <button
                      onClick={() => handleCreatePurchaseClick(vendor)}
                      className="flex items-center gap-1 bg-emerald-500 text-white px-3 py-2 rounded text-sm"
                    >
                      <Plus size={14} /> Purchase
                    </button>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleEditClick(vendor)}
                      className="p-2 border rounded-lg hover:bg-blue-50 text-blue-600"
                    >
                      <Pencil size={16} />
                    </button>

                    <button
                      onClick={() => {
                        setSelectedVendor(vendor);
                        setDeleteModal(true);
                      }}
                      className="p-2 border rounded-lg hover:bg-red-50 text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* FORM */}

        {formMode && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 montserrat-font-1">
            <div className="w-[650px] bg-white rounded-xl shadow-xl p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-semibold">
                  {formMode === "add" ? "Add Vendor" : "Edit Vendor"}
                </h3>

                <button
                  onClick={closeForm}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                {/* Vendor Name */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">
                    Vendor Name
                  </label>
                  <input
                    name="vendorName"
                    value={form.vendorName}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    required
                  />
                </div>

                {/* Vendor Type */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">
                    Vendor Type
                  </label>
                  <ScrollableSelect
                    name="vendorType"
                    value={form.vendorType}
                    onChange={handleChange}
                    options={VENDOR_TYPES}
                    placeholder="Select Vendor Type"
                    required
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">
                    Product Category
                  </label>
                  <ScrollableSelect
                    name="vendorCategory"
                    value={form.vendorCategory}
                    onChange={handleChange}
                    options={PRODUCT_CATEGORIES}
                    placeholder="Select Category"
                    required
                  />
                </div>

                {/* Mobile */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">
                    Mobile Number
                  </label>
                  <input
                    name="mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    required
                  />
                </div>

                {/* GST */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">
                    GST Number
                  </label>
                  <input
                    name="gst"
                    value={form.gst}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                {/* State */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">
                    State
                  </label>
                  <input
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                {/* City */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">
                    City
                  </label>
                  <input
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                {/* Address (full width) */}
                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-1">
                    Vendor Address
                  </label>
                  <textarea
                    name="vendorAddress"
                    value={form.vendorAddress}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    rows="2"
                    required
                  />
                </div>

                {/* Buttons */}
                <div className="col-span-2 flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeForm}
                    className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-100"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* delete model */}

        {deleteModal && selectedVendor && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 montserrat-font-1">
            <div className="bg-white rounded-xl shadow-xl w-[400px] p-6">
              <h3 className="text-lg font-semibold mb-4">Delete Vendor</h3>

              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete this vendor?
              </p>

              {/* Vendor Info */}

              <div className="bg-gray-50 border rounded-lg p-4 text-sm mb-5">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-500">Vendor Name</span>
                  <span className="font-medium">
                    {selectedVendor.vendorName}
                  </span>
                </div>

                <div className="flex justify-between mb-2">
                  <span className="text-gray-500">Vendor ID</span>
                  <span>{selectedVendor.id}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-500">Vendor Type</span>
                  <span>{selectedVendor.vendorType}</span>
                </div>
              </div>

              {/* Buttons */}

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setDeleteModal(false);
                    setSelectedVendor(null);
                  }}
                  className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-100"
                >
                  Cancel
                </button>

                <button
                  onClick={confirmDelete}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm"
                >
                  Confirm Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default VendorManagement;
